/**
 * Canonical API Client
 * Main entry point for all API interactions
 */

import { getServicesConfig } from "../config/services-config";
import { HttpClient } from "./client";
import { AuthService } from "./services/auth-service";
import { HealthService } from "./services/health-service";
import { PackagesService } from "./services/packages-service";
import { TenantService } from "./services/tenant-service";
import { TokensService } from "./services/tokens-service";
import type {
	AnalyticsMetrics,
	ApiToken,
	CreateTokenRequest,
	HealthCheckResponse,
	InviteTeamMemberRequest,
	LoginRequest,
	LoginResponse,
	LogoutRequest,
	Package,
	PackageListParams,
	PaginatedResponse,
	RefreshTokenResponse,
	TeamMember,
	Tenant,
	TokenVerificationResponse,
	UpdateTeamMemberRequest,
} from "./types";

export class ApiClient {
	private httpClient: HttpClient;
	private authService: AuthService;
	private tenantService: TenantService;
	private packagesService: PackagesService;
	private tokensService: TokensService;
	private healthService: HealthService;
	private config = getServicesConfig();

	constructor() {
		this.httpClient = new HttpClient({
			timeout: this.config.timeout,
			retries: this.config.retries,
		});

		this.authService = new AuthService(this.httpClient);
		this.tenantService = new TenantService(this.httpClient);
		this.packagesService = new PackagesService(this.httpClient);
		this.tokensService = new TokensService(this.httpClient);
		this.healthService = new HealthService(this.httpClient);
	}

	// ==================== Authentication API ====================

	async login(request: LoginRequest): Promise<LoginResponse> {
		return await this.authService.login(request);
	}

	async refreshToken(refreshToken?: string): Promise<RefreshTokenResponse> {
		const token =
			refreshToken || localStorage.getItem("artify_refresh_token") || "";
		return await this.authService.refreshToken({ refreshToken: token });
	}

	async logout(request?: LogoutRequest): Promise<void> {
		return await this.authService.logout(request);
	}

	async verifyToken(): Promise<TokenVerificationResponse> {
		return await this.authService.verifyToken();
	}

	async changePassword(
		currentPassword: string,
		newPassword: string,
	): Promise<void> {
		return this.authService.changePassword(currentPassword, newPassword);
	}

	// ==================== Health Check API ====================

	async getHealth(): Promise<HealthCheckResponse> {
		return this.healthService.checkApiHealth();
	}

	async isApiAvailable(): Promise<boolean> {
		return this.healthService.isApiAvailable();
	}

	// ==================== Tenant API ====================

	async getTenant(tenantSlug: string): Promise<Tenant> {
		return await this.tenantService.getTenantInfo(tenantSlug);
	}

	async updateTenant(
		tenantSlug: string,
		data: Partial<Tenant>,
	): Promise<Tenant> {
		return await this.tenantService.updateTenant(tenantSlug, data);
	}

	// ==================== Package API ====================

	async listPackages(
		tenantSlug: string,
		params?: PackageListParams,
	): Promise<PaginatedResponse<Package>> {
		return await this.packagesService.getPackages(tenantSlug, params);
	}

	async getPackage(
		tenantSlug: string,
		ecosystem: string,
		packageName: string,
	): Promise<Package> {
		return await this.packagesService.getPackage(
			tenantSlug,
			ecosystem,
			packageName,
		);
	}

	async deletePackage(
		tenantSlug: string,
		ecosystem: string,
		packageName: string,
	): Promise<void> {
		await this.packagesService.deletePackage(
			tenantSlug,
			ecosystem,
			packageName,
		);
	}

	// ==================== Token API ====================

	async listTokens(tenantSlug: string): Promise<ApiToken[]> {
		return await this.tokensService.getTokens(tenantSlug);
	}

	async createToken(
		tenantSlug: string,
		data: CreateTokenRequest,
	): Promise<{ token: ApiToken; secretToken: string }> {
		return await this.tokensService.createToken(tenantSlug, data);
	}

	async revokeToken(tenantSlug: string, tokenId: string): Promise<void> {
		await this.tokensService.deleteToken(tenantSlug, tokenId);
	}

	// ==================== Team API ====================

	async listTeamMembers(tenantSlug: string): Promise<TeamMember[]> {
		return await this.tenantService.getTeamMembers(tenantSlug);
	}

	async inviteTeamMember(
		tenantSlug: string,
		data: InviteTeamMemberRequest,
	): Promise<void> {
		await this.tenantService.inviteTeamMember(tenantSlug, data);
	}

	async removeTeamMember(tenantSlug: string, memberId: string): Promise<void> {
		await this.tenantService.removeTeamMember(tenantSlug, memberId);
	}

	async updateTeamMemberRole(
		tenantSlug: string,
		memberId: string,
		data: UpdateTeamMemberRequest,
	): Promise<void> {
		await this.tenantService.updateTeamMemberRole(tenantSlug, memberId, data);
	}

	// ==================== Analytics API ====================

	async getAnalytics(
		tenantSlug: string,
		timeframe: string = "7d",
	): Promise<AnalyticsMetrics> {
		return await this.tenantService.getAnalytics(tenantSlug, timeframe);
	}

	async getUsageStats(tenantSlug: string): Promise<any> {
		return await this.tenantService.getUsageStats(tenantSlug);
	}

	// ==================== Utility Methods ====================

	public clearCache(): void {
		this.httpClient.clearCache();
		this.healthService.clearCache();
	}

	public getBaseURL(): string {
		return this.config.services.auth; // Return auth service as default
	}

	public getConfig() {
		return this.config;
	}

	// ==================== Legacy Compatibility ====================

	public get<T>(
		endpoint: string,
		options?: { params?: Record<string, any> },
	): Promise<T> {
		const url = endpoint.startsWith("http")
			? endpoint
			: `${this.config.services.auth}${endpoint}`;

		if (options?.params) {
			const searchParams = new URLSearchParams(options.params).toString();
			const separator = url.includes("?") ? "&" : "?";
			const fullUrl = `${url}${separator}${searchParams}`;
			return this.httpClient.get<T>(fullUrl);
		}

		return this.httpClient.get<T>(url);
	}
}

// Export singleton instance
export const apiClient = new ApiClient();

export type * from "./errors";
// Export types for convenience
export type * from "./types";
